import React, {FC, useEffect, useMemo} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import Select, {MultiValue} from 'react-select'
import {useMailTemplateOptions} from '../shared/useMailTemplateOptions'
import {
  conditionArity,
  conditionOperatorsFor,
  initialQuestionForm,
  IQuestionFormValues,
  IQuestionModel,
  isChoiceType,
  parseOptions,
  QUESTION_INPUT_TYPES,
  toQuestionFormValues,
  toQuestionModel,
  validationQuestion,
} from './QuestionModel'

type Props = {
  show: boolean
  editingItem: IQuestionModel | null
  loading: boolean
  onClose: () => void
  onSubmit: (question: IQuestionModel) => void
}

type SelectOption = {value: string; label: string}

const BOOLEAN_OPTIONS: SelectOption[] = [
  {value: 'true', label: 'Evet'},
  {value: 'false', label: 'Hayır'},
]

export const QuestionFormModal: FC<Props> = ({show, editingItem, loading, onClose, onSubmit}) => {
  const isEditing = !!editingItem

  const formik = useFormik<IQuestionFormValues>({
    initialValues: initialQuestionForm,
    validationSchema: validationQuestion,
    onSubmit: (values) => onSubmit(toQuestionModel(values)),
  })

  const {options: mailTemplateOptions, loading: mailTemplatesLoading} = useMailTemplateOptions(
    show,
    editingItem?.failMailTemplateId
  )

  // Modal her açıldığında formu yeni kayıt / düzenlenen kayıt ile doldur
  useEffect(() => {
    if (!show) return
    formik.resetForm({values: editingItem ? toQuestionFormValues(editingItem) : initialQuestionForm})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingItem])

  // Listede olmayan eski bir tip ile kayıtlı soru düzenlenirken o tip de seçilebilsin
  const inputTypes = QUESTION_INPUT_TYPES.some((type) => type.value === formik.values.inputType)
    ? QUESTION_INPUT_TYPES
    : [...QUESTION_INPUT_TYPES, {value: formik.values.inputType, label: formik.values.inputType}]

  const operators = useMemo(
    () => conditionOperatorsFor(formik.values.inputType),
    [formik.values.inputType]
  )

  const arity = formik.values.conditionOperator
    ? conditionArity(formik.values.conditionOperator)
    : 'NONE'
  const hasCondition = !!formik.values.conditionOperator

  /** Seçenekli sorularda koşul değerleri sorunun seçeneklerinden seçilir */
  const answerOptions = useMemo<SelectOption[]>(() => {
    if (isChoiceType(formik.values.inputType)) {
      return parseOptions(formik.values.optionsText).map((option) => ({
        value: option,
        label: option,
      }))
    }
    return formik.values.inputType === 'BOOLEAN' ? BOOLEAN_OPTIONS : []
  }, [formik.values.inputType, formik.values.optionsText])

  const fieldError = (field: keyof IQuestionFormValues) =>
    formik.touched[field] && formik.errors[field] ? (formik.errors[field] as string) : undefined

  /** Cevap tipi değişince o tipte geçerli olmayan koşul temizlenir */
  const handleInputTypeChange = (inputType: string) => {
    formik.setFieldValue('inputType', inputType)
    const stillValid = conditionOperatorsFor(inputType).some(
      (operator) => operator.value === formik.values.conditionOperator
    )
    if (!stillValid) {
      formik.setFieldValue('conditionOperator', '')
      formik.setFieldValue('failMailTemplateId', '')
    }
    formik.setFieldValue('conditionValue', '')
    formik.setFieldValue('conditionValues', [])
  }

  const handleOperatorChange = (operator: string) => {
    formik.setFieldValue('conditionOperator', operator)
    formik.setFieldValue('conditionValue', '')
    formik.setFieldValue('conditionValues', [])
    if (!operator) {
      formik.setFieldValue('failMailTemplateId', '')
    }
  }

  const singleValueInput = () => {
    if (answerOptions.length > 0) {
      return (
        <select
          className={`form-select ${fieldError('conditionValue') ? 'is-invalid' : ''}`}
          {...formik.getFieldProps('conditionValue')}
        >
          <option value=''>Seçiniz</option>
          {answerOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )
    }
    const type =
      formik.values.inputType === 'NUMBER'
        ? 'number'
        : formik.values.inputType === 'DATE'
        ? 'date'
        : 'text'
    // Formik number input'ta değeri sayıya çevirir; koşul değeri her zaman metin olarak tutulur
    return (
      <input
        type={type}
        name='conditionValue'
        className={`form-control ${fieldError('conditionValue') ? 'is-invalid' : ''}`}
        placeholder='Beklenen değer'
        value={formik.values.conditionValue}
        onChange={(event) => formik.setFieldValue('conditionValue', event.target.value)}
        onBlur={formik.handleBlur}
      />
    )
  }

  const multiValueInput = () => {
    if (answerOptions.length > 0) {
      const selected = answerOptions.filter((option) =>
        formik.values.conditionValues.includes(option.value)
      )
      return (
        <Select<SelectOption, true>
          isMulti
          options={answerOptions}
          value={selected}
          onChange={(values: MultiValue<SelectOption>) => {
            formik.setFieldValue(
              'conditionValues',
              values.map((option) => option.value)
            )
            formik.setFieldTouched('conditionValues', true, false)
          }}
          placeholder='Kabul edilen cevapları seçiniz'
          noOptionsMessage={() => 'Önce soru seçeneklerini giriniz'}
          classNamePrefix='react-select'
        />
      )
    }
    return (
      <textarea
        rows={3}
        className='form-control'
        placeholder={'Her satıra bir değer yazınız'}
        value={formik.values.conditionValues.join('\n')}
        onChange={(event) =>
          formik.setFieldValue('conditionValues', parseOptions(event.target.value))
        }
        onBlur={() => formik.setFieldTouched('conditionValues', true)}
      />
    )
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      size='lg'
      dialogClassName='modal-dialog-centered'
      backdrop='static'
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='modal-header'>
          <h5 className='modal-title'>{isEditing ? 'Soru Düzenle' : 'Yeni Soru'}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='modal-body'>
          <div className='mb-5'>
            <label className='form-label fw-bold required'>Soru</label>
            <input
              type='text'
              autoFocus
              className={`form-control ${fieldError('label') ? 'is-invalid' : ''}`}
              placeholder='Örn: İş deneyimi süreniz'
              {...formik.getFieldProps('label')}
            />
            {fieldError('label') && <div className='invalid-feedback'>{fieldError('label')}</div>}
          </div>

          <div className='mb-5'>
            <label className='form-label fw-bold required'>Cevap Tipi</label>
            <select
              className={`form-select ${fieldError('inputType') ? 'is-invalid' : ''}`}
              name='inputType'
              value={formik.values.inputType}
              onBlur={formik.handleBlur}
              onChange={(event) => handleInputTypeChange(event.target.value)}
            >
              {inputTypes
                .filter((type) => type.value)
                .map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
            </select>
            {fieldError('inputType') && (
              <div className='invalid-feedback'>{fieldError('inputType')}</div>
            )}
          </div>

          {isChoiceType(formik.values.inputType) && (
            <div className='mb-5'>
              <label className='form-label fw-bold required'>Seçenekler</label>
              <textarea
                rows={5}
                className={`form-control ${fieldError('optionsText') ? 'is-invalid' : ''}`}
                placeholder={'Her satıra bir seçenek yazınız\nÖrn:\nLinkedIn\nŞirket web sitesi\nArkadaş tavsiyesi'}
                {...formik.getFieldProps('optionsText')}
              />
              {fieldError('optionsText') ? (
                <div className='invalid-feedback'>{fieldError('optionsText')}</div>
              ) : (
                <div className='form-text'>Her satır bir seçenek olarak gösterilir.</div>
              )}
            </div>
          )}

          <div className='form-check form-switch form-check-custom form-check-solid mb-5'>
            <input
              id='question-required'
              type='checkbox'
              className='form-check-input'
              checked={formik.values.required}
              onChange={(event) => formik.setFieldValue('required', event.target.checked)}
            />
            <label className='form-check-label fw-bold' htmlFor='question-required'>
              Zorunlu soru
            </label>
          </div>

          <div className='separator separator-dashed my-5'></div>

          <div className='mb-3'>
            <span className='fw-bolder fs-5 text-gray-800'>Koşul ve Otomatik Mail</span>
            <div className='text-muted fs-7'>
              Adayın cevabı koşulu sağlamazsa seçtiğiniz şablonla otomatik mail gönderilir.
              Başvuru yine kaydedilir.
            </div>
          </div>

          <div className='row g-5'>
            <div className={arity === 'NONE' ? 'col-12' : 'col-md-5'}>
              <label className='form-label fw-bold'>Koşul</label>
              <select
                className='form-select'
                name='conditionOperator'
                value={formik.values.conditionOperator}
                onBlur={formik.handleBlur}
                onChange={(event) => handleOperatorChange(event.target.value)}
              >
                <option value=''>Koşul yok</option>
                {operators.map((operator) => (
                  <option key={operator.value} value={operator.value}>
                    {operator.label}
                  </option>
                ))}
              </select>
            </div>

            {arity === 'SINGLE' && (
              <div className='col-md-7'>
                <label className='form-label fw-bold required'>Beklenen Cevap</label>
                {singleValueInput()}
                {fieldError('conditionValue') && (
                  <div className='text-danger fs-7 mt-1'>{fieldError('conditionValue')}</div>
                )}
              </div>
            )}

            {arity === 'MULTI' && (
              <div className='col-md-7'>
                <label className='form-label fw-bold required'>Kabul Edilen Cevaplar</label>
                {multiValueInput()}
                {fieldError('conditionValues') && (
                  <div className='text-danger fs-7 mt-1'>{fieldError('conditionValues')}</div>
                )}
              </div>
            )}

            {hasCondition && (
              <div className='col-12'>
                <label className='form-label fw-bold'>Koşul Sağlanmazsa Gönderilecek Mail</label>
                <select
                  className='form-select'
                  disabled={mailTemplatesLoading}
                  {...formik.getFieldProps('failMailTemplateId')}
                >
                  <option value=''>Mail gönderilmesin</option>
                  {mailTemplateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className='form-text'>
                  {mailTemplatesLoading
                    ? 'Şablonlar yükleniyor...'
                    : mailTemplateOptions.length === 0
                    ? 'Henüz mail şablonu yok. Mail Şablonu ekranından oluşturabilirsiniz.'
                    : 'Şablonlar Mail Şablonu ekranından yönetilir.'}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Lütfen bekleyin...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : isEditing ? (
              'Güncelle'
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}
